import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import { VM_Colors } from "../../Styles/VMStyles";
import { toTitleCase } from "../../Util/UtilHelper";
import {
  formatAssignedTo,
  formatCreatedBy,
  formatCreatedDate,
} from "./todayUpdateMeta";

const STATUS_STYLES = {
  Pending: { bg: "#FFF4E5", color: "#B45309" },
  "In Progress": { bg: "#E8F1FB", color: VM_Colors.primary },
  Complete: { bg: "#E8F7EA", color: VM_Colors.success },
  Completed: { bg: "#E8F7EA", color: VM_Colors.success },
  deleted: { bg: VM_Colors.error_transparent, color: VM_Colors.error },
};

const MetaChip = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <Tooltip title={`${label}: ${value}`} arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.35,
          borderRadius: 1.25,
          bgcolor: VM_Colors.white1,
          border: `1px solid ${VM_Colors.light_gray}`,
          maxWidth: "100%",
        }}
      >
        <Box sx={{ display: "flex", color: VM_Colors.teal, "& svg": { fontSize: 13 } }}>
          {icon}
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            color: VM_Colors.steel_gray,
            fontWeight: 600,
            letterSpacing: 0.2,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: 11.5,
            color: "#334155",
            fontWeight: 600,
            maxWidth: 140,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default function TodayUpdateItem({
  title,
  status,
  createdDate,
  dueDate,
  createdBy,
  assignedTo,
  leading,
  onClick,
}) {
  const statusStyle = STATUS_STYLES[status] || {
    bg: VM_Colors.white1,
    color: VM_Colors.teal,
  };
  const dateLabel = formatCreatedDate(createdDate)
    ? formatCreatedDate(createdDate)
    : formatCreatedDate(dueDate);
  const datePrefix = formatCreatedDate(createdDate) ? "Created" : dueDate ? "Due" : "";
  const createdByName = formatCreatedBy(createdBy);
  const assignedName = formatAssignedTo(assignedTo);

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: 2,
        border: `1px solid ${VM_Colors.light_gray}`,
        bgcolor: VM_Colors.white,
        cursor: "pointer",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        "&:last-of-type": { mb: 0 },
        "&:hover": {
          borderColor: `${VM_Colors.primary}66`,
          bgcolor: VM_Colors.white1,
          boxShadow: "0 4px 12px rgba(30, 58, 138, 0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.25}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          {leading}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14.5,
              lineHeight: 1.35,
              color: "#0F172A",
              letterSpacing: "-0.01em",
            }}
          >
            {toTitleCase(title || "")}
          </Typography>
        </Stack>
        {status && (
          <Chip
            label={status}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: statusStyle.bg,
              color: statusStyle.color,
              borderRadius: 1.25,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.15 }}>
        <MetaChip
          icon={<CalendarTodayOutlinedIcon />}
          label={datePrefix}
          value={dateLabel}
        />
        <MetaChip
          icon={<PersonOutlineOutlinedIcon />}
          label="Created by"
          value={createdByName}
        />
        <MetaChip
          icon={<AssignmentIndOutlinedIcon />}
          label="Assigned to"
          value={assignedName}
        />
      </Stack>
    </Box>
  );
}
